import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Table } from 'react-bootstrap'
import moment from 'moment'
import mapTimeZoneToCountry from '../../../utils/timezone'
import { getOrderNotificationHistory, searchOrderMessages } from '../../../utils/order-webapi'
import { handleError } from '../../../utils/handle-error'

const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}

/**
 * Render SMS Log
 */
export default class NotificationsLog extends Component {
    constructor(...args) {
        super(...args)
        this.state = {
            notificationLog: [],
        }
    }

    componentDidMount() {
        if (this.props.order) {
            this.updateNotificationLog()
            this.updateCommsMessages()
        }
    }

    updateNotificationLog = () => {
        // Retrieve a list of notifications sent to the consumer
        // regarding this order
        getOrderNotificationHistory(this.props.order.token)
            .then((notificationLog) => {
                this.updateNotificationState(notificationLog)
            })
            .catch(handleError)
    }

    updateCommsMessages = () => {
        // Retrieve a list of messages sent to the consumer through Comms
        // regarding this order
        searchOrderMessages(this.props.order.token)
            .then((response) => {
                const filteredResults = (response.results || []).filter((message) => {
                    // Hide messages that have been sent with dryRun option to avoid confusions
                    return !message.options.dryRun
                })
                const formattedMessages = this.formatCommsMessages(filteredResults)
                this.updateNotificationState(formattedMessages)
            })
            .catch(handleError)
    }

    updateNotificationState(newNotifications) {
        this.setState((prevState) => ({
            notificationLog: [...prevState.notificationLog, ...newNotifications].sort(
                (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
            ),
        }))
    }

    formatCommsMessages = (messages) => {
        // Transform comms messages to match notification log structure
        const formattedMessages = []

        messages.forEach((message) => {
            if (message.sendouts && message.sendouts.length > 0) {
                message.sendouts.forEach((sendout) => {
                    const sendoutType = this.getSendoutType(sendout.destination)

                    const content = sendoutType === 'EMAIL' ? '...' : sendout.body || 'UNKNOWN'

                    formattedMessages.push({
                        id: sendout.id,
                        type: `${sendoutType}:${this.getMessageTemplate(message)}`,
                        event: message.status || 'UNKNOWN',
                        timestamp: sendout.createdAt,
                        to: sendout.destination || 'UNKNOWN',
                        message: content,
                    })
                })
            }
        })

        return formattedMessages
    }

    getSendoutType = (destination) => {
        if (/^\+?[0-9]+$/.test(destination)) {
            return 'SMS'
        }

        if (/^[a-zA-Z0-9._%+?-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/i.test(destination)) {
            return 'EMAIL'
        }

        if (destination.length) {
            return 'NOTIFICATION'
        }

        return 'UNKNOWN'
    }

    getMessageTemplate = (message) => {
        const templateName = message.options && message.options.template && message.options.template.name.toUpperCase()
        return templateName || 'CUSTOM'
    }

    renderRow = (notification) => {
        const { order } = this.props

        const timestamp =
            notification.timestamp == null
                ? 'No timestamp'
                : moment(notification.timestamp)
                      .tz(mapTimeZoneToCountry(order.deliveryAddress.countryCode))
                      .format('YYYY-MM-DD HH:mm')

        const isPhoneNumber = notification.type.toLowerCase().startsWith('sms:')

        return (
            <tr key={notification.id + notification.timestamp + notification.event}>
                <td>{notification.type}</td>
                <td>{notification.event}</td>
                <td style={cellStyle}>{timestamp}</td>
                {isPhoneNumber ? (
                    <td>
                        <a href={`tel:${notification.to}`}>{notification.to}</a>
                    </td>
                ) : (
                    <td>{notification.to}</td>
                )}
                <td>{notification.message}</td>
                <td>{notification.extra != null ? notification.extra.reason : ''}</td>
            </tr>
        )
    }

    render() {
        const { notificationLog } = this.state

        if (notificationLog == null || notificationLog.length === 0) {
            return null
        }

        return (
            <Table striped responsive>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Timestamp</th>
                        <th>To</th>
                        <th>Message</th>
                        <th>Info</th>
                    </tr>
                </thead>
                <tbody>{notificationLog.map(this.renderRow)}</tbody>
            </Table>
        )
    }
}

NotificationsLog.propTypes = {
    order: PropTypes.object.isRequired,
}
